import { useEffect, useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import {
  integrations,
  type IntegrationDetail,
  type IntegrationList,
  type OutboxRow,
  type OutboxStatus,
} from '../../services/integrations'

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Request failed')
const date = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')
const humanize = (value: string) => value.replaceAll('_', ' ')
const card =
  'rounded-2xl bg-white dark:bg-[#1a1425] shadow-sm border border-gray-100 dark:border-[#2d2236]'
const selectClass =
  'rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1425] px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]'
const alertClass =
  'rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-600 dark:text-red-400'
const sectionTitle = 'font-heading text-lg font-semibold text-gray-900 dark:text-white'
const cell = 'px-3 py-2 text-sm text-gray-700 dark:text-gray-200 align-top'
const headCell =
  'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400'

const GREEN = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
const AMBER = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
const RED = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
const STATUS_COLORS: Record<string, string> = {
  active: GREEN,
  succeeded: GREEN,
  sent: GREEN,
  processed: GREEN,
  connecting: AMBER,
  running: AMBER,
  pending: AMBER,
  held: AMBER,
  queued: AMBER,
  error: RED,
  failed: RED,
  in_doubt: RED,
}
const PROVIDERS: Record<string, string> = {
  bigcommerce: 'BigCommerce',
  shipstation: 'ShipStation',
  easypost_carrier: 'Own carrier',
}
const OUTBOX_STATUSES: OutboxStatus[] = [
  'held',
  'in_doubt',
  'failed',
  'pending',
  'sent',
  'discarded',
]

function Badge({ value }: { value: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[value] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
    >
      {humanize(value)}
    </span>
  )
}

function Counts({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0)
  return (
    <div className="space-y-2">
      <h3 className={sectionTitle}>{title}</h3>
      {entries.length ? (
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {entries.map(([key, value]) => (
            <div key={key} className="rounded-xl bg-gray-50 dark:bg-[#120e1b] p-3">
              <dt className="text-xs capitalize text-gray-500 dark:text-gray-400">
                {humanize(key)}
              </dt>
              <dd className="text-xl font-semibold text-gray-900 dark:text-white">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-gray-400">None.</p>
      )}
    </div>
  )
}

function OutboxTable({
  rows,
  storeNames,
}: {
  rows: OutboxRow[]
  storeNames?: Record<string, string>
}) {
  if (!rows.length) return <p className="text-sm text-gray-400">No writes in this status.</p>
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100 dark:divide-[#2d2236]">
        <thead>
          <tr>
            {storeNames && <th className={headCell}>Store</th>}
            <th className={headCell}>Kind</th>
            <th className={headCell}>Status</th>
            <th className={headCell}>Target</th>
            <th className={headCell}>Change</th>
            <th className={headCell}>Attempts</th>
            <th className={headCell}>Created</th>
            <th className={headCell}>Last error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-[#2d2236]">
          {rows.map((row) => (
            <tr key={row.id}>
              {storeNames && (
                <td className={cell}>
                  {storeNames[row.integration_id] && (
                    <span className="block font-medium">{storeNames[row.integration_id]}</span>
                  )}
                  <span className="block font-mono text-xs text-gray-500 dark:text-gray-400 break-all">
                    integration {row.integration_id}
                  </span>
                  <span className="block font-mono text-xs text-gray-500 dark:text-gray-400 break-all">
                    shop {row.shop_id}
                  </span>
                </td>
              )}
              <td className={cell}>
                {humanize(row.kind)}
                {row.reason && (
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {row.reason}
                  </span>
                )}
              </td>
              <td className={cell}>
                <Badge value={row.status} />
              </td>
              <td className={`${cell} font-mono text-xs`}>
                {row.order_id
                  ? `order ${row.order_id}`
                  : [row.target_product_id, row.target_variant_id].filter(Boolean).join(' / ') ||
                    '—'}
              </td>
              <td className={cell}>
                {row.delta === null ? '—' : row.delta > 0 ? `+${row.delta}` : row.delta}
              </td>
              <td className={cell}>{row.attempts}</td>
              <td className={cell}>{date(row.created_at)}</td>
              <td className={`${cell} max-w-xs break-words text-xs`}>{row.last_error ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Detail({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<IntegrationDetail | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    let current = true
    setDetail(null)
    setError('')
    integrations
      .detail(id)
      .then((result) => current && setDetail(result))
      .catch((cause) => current && setError(errorMessage(cause)))
    return () => {
      current = false
    }
  }, [id])

  return (
    <section className={`${card} p-5 space-y-6`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-gray-900 dark:text-white">
            {detail?.integration.display_name ?? 'Integration'}
          </h2>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400 break-all">{id}</p>
        </div>
        <CustomButton variant="secondary" size="sm" onClick={onClose}>
          Close
        </CustomButton>
      </div>
      {error && (
        <p role="alert" className={alertClass}>
          {error}
        </p>
      )}
      {!detail && !error && <p className="text-sm text-gray-400">Loading…</p>}
      {detail && (
        <>
          <dl className="grid grid-cols-1 gap-4 rounded-xl bg-gray-50 dark:bg-[#120e1b] p-4 sm:grid-cols-3">
            {(
              [
                ['Provider', PROVIDERS[detail.integration.provider] ?? detail.integration.provider],
                ['Status', <Badge key="s" value={detail.integration.status} />],
                ['Shop', detail.integration.shop_name ?? detail.integration.shop_id],
                ['Account', detail.integration.external_account_id],
                [
                  'Webhooks',
                  detail.integration.webhooks_registered ? 'Registered' : 'Not registered',
                ],
                ['Synced listings', detail.integration.synced_listings],
                ['Last sync', date(detail.integration.last_sync_at)],
                ['Next reconcile', date(detail.integration.next_reconcile_at)],
                ['Connected', date(detail.integration.created_at)],
              ] as [string, React.ReactNode][]
            ).map(([label, value]) => (
              <div key={label} className="flex flex-col gap-0.5">
                <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100 break-all">{value}</dd>
              </div>
            ))}
          </dl>
          {detail.integration.last_error && (
            <p role="alert" className={alertClass}>
              {date(detail.integration.last_error_at)}: {detail.integration.last_error}
            </p>
          )}
          <Counts title="Outside writes" counts={detail.outbox_by_status} />
          <Counts title="Open gaps" counts={detail.gaps_by_kind} />
          <div className="space-y-2">
            <h3 className={sectionTitle}>Writes needing a decision</h3>
            <OutboxTable rows={detail.attention} />
          </div>
          <div className="space-y-2">
            <h3 className={sectionTitle}>Recent syncs</h3>
            {detail.recent_runs.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 dark:divide-[#2d2236]">
                  <thead>
                    <tr>
                      <th className={headCell}>Kind</th>
                      <th className={headCell}>Status</th>
                      <th className={headCell}>Processed</th>
                      <th className={headCell}>Created / updated / unchanged</th>
                      <th className={headCell}>Skipped / failed</th>
                      <th className={headCell}>Started</th>
                      <th className={headCell}>Finished</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#2d2236]">
                    {detail.recent_runs.map((run) => (
                      <tr key={run.id}>
                        <td className={cell}>{humanize(run.kind)}</td>
                        <td className={cell}>
                          <Badge value={run.status} />
                          {run.error_summary && (
                            <span className="block max-w-xs break-words text-xs text-red-600 dark:text-red-400">
                              {run.error_summary}
                            </span>
                          )}
                        </td>
                        <td className={cell}>
                          {run.processed} / {run.total}
                        </td>
                        <td className={cell}>
                          {run.created_count} / {run.updated_count} / {run.unchanged_count}
                        </td>
                        <td className={cell}>
                          {run.skipped_count} / {run.failed_count}
                        </td>
                        <td className={cell}>{date(run.started_at)}</td>
                        <td className={cell}>{date(run.finished_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No syncs yet.</p>
            )}
          </div>
          <div className="space-y-2">
            <h3 className={sectionTitle}>Recent webhooks</h3>
            {detail.recent_events.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 dark:divide-[#2d2236]">
                  <thead>
                    <tr>
                      <th className={headCell}>Scope</th>
                      <th className={headCell}>Resource</th>
                      <th className={headCell}>Status</th>
                      <th className={headCell}>Attempts</th>
                      <th className={headCell}>Received</th>
                      <th className={headCell}>Last error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#2d2236]">
                    {detail.recent_events.map((event) => (
                      <tr key={event.id}>
                        <td className={`${cell} font-mono text-xs`}>{event.scope}</td>
                        <td className={`${cell} font-mono text-xs`}>{event.resource_id ?? '—'}</td>
                        <td className={cell}>
                          <Badge value={event.status} />
                        </td>
                        <td className={cell}>{event.attempts}</td>
                        <td className={cell}>{date(event.received_at)}</td>
                        <td className={`${cell} max-w-xs break-words text-xs`}>
                          {event.last_error ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No webhooks received.</p>
            )}
          </div>
          <details className="text-sm text-gray-700 dark:text-gray-200">
            <summary className="cursor-pointer font-medium">Settings and key scopes</summary>
            <pre className="mt-2 overflow-x-auto rounded-xl bg-gray-50 dark:bg-[#120e1b] p-4 text-xs">
              {JSON.stringify({ settings: detail.settings, scopes: detail.scopes }, null, 2)}
            </pre>
          </details>
        </>
      )}
    </section>
  )
}

export function Integrations() {
  const canRead = usePermission('integrations', 'read')
  const [tab, setTab] = useState<'connections' | 'outbox'>('connections')
  const [status, setStatus] = useState('')
  const [provider, setProvider] = useState('')
  const [page, setPage] = useState(1)
  const [list, setList] = useState<IntegrationList | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [outboxStatus, setOutboxStatus] = useState<OutboxStatus>('held')
  const [outbox, setOutbox] = useState<OutboxRow[] | null>(null)
  const [writesEnabled, setWritesEnabled] = useState<boolean | null>(null)
  const [error, setError] = useState('')

  // Each effect run supersedes the last, so a slow earlier response can't overwrite newer filters.
  useEffect(() => {
    if (!canRead || tab !== 'connections') return
    let current = true
    setError('')
    integrations
      .list({ status, provider, page })
      .then((result) => {
        if (!current) return
        setList(result)
        setWritesEnabled(result.writes_enabled)
      })
      .catch((cause) => current && setError(errorMessage(cause)))
    return () => {
      current = false
    }
  }, [canRead, tab, status, provider, page])

  useEffect(() => {
    if (!canRead || tab !== 'outbox') return
    let current = true
    setError('')
    setOutbox(null)
    integrations
      .outbox(outboxStatus)
      .then((result) => {
        if (!current) return
        setOutbox(result.items)
        setWritesEnabled(result.writes_enabled)
      })
      .catch((cause) => current && setError(errorMessage(cause)))
    return () => {
      current = false
    }
  }, [canRead, tab, outboxStatus])

  if (!canRead)
    return (
      <p role="alert" className={alertClass}>
        You do not have permission to view integrations.
      </p>
    )

  const pages = list ? Math.max(1, Math.ceil(list.total / list.page_size)) : 1
  const tabClass = (active: boolean) =>
    `rounded-full px-4 py-1.5 text-sm font-medium ${active ? 'bg-[#7f4f9a] text-white' : 'bg-gray-100 text-gray-700 dark:bg-[#2d2236] dark:text-gray-200'}`

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
          Store integrations
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Connected BigCommerce stores and ShipStation accounts, their syncs, webhooks and the
          writes Barta queued for them. Read only; credentials are never shown.
        </p>
      </div>
      {writesEnabled !== null && (
        <p
          className={`rounded-xl px-4 py-3 text-sm ${writesEnabled ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'}`}
        >
          {writesEnabled
            ? 'Outside writes are on. Queued stock changes and ShipStation orders are being sent.'
            : 'Outside writes are off. Barta reads from stores but sends nothing; stock changes wait in the outbox as held.'}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          className={tabClass(tab === 'connections')}
          onClick={() => setTab('connections')}
        >
          Connections
        </button>
        <button
          type="button"
          className={tabClass(tab === 'outbox')}
          onClick={() => setTab('outbox')}
        >
          Outbox
        </button>
      </div>
      {error && (
        <p role="alert" className={alertClass}>
          {error}
        </p>
      )}
      {tab === 'connections' && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Status
              <select
                className={selectClass}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All</option>
                <option value="connecting">Connecting</option>
                <option value="active">Active</option>
                <option value="error">Error</option>
                <option value="disconnected">Disconnected</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Provider
              <select
                className={selectClass}
                value={provider}
                onChange={(e) => {
                  setProvider(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All</option>
                <option value="bigcommerce">BigCommerce</option>
                <option value="shipstation">ShipStation</option>
              </select>
            </label>
          </div>
          <section className={`${card} overflow-x-auto`}>
            <table className="min-w-full divide-y divide-gray-100 dark:divide-[#2d2236]">
              <thead>
                <tr>
                  <th className={headCell}>Store</th>
                  <th className={headCell}>Shop</th>
                  <th className={headCell}>Status</th>
                  <th className={headCell}>Listings</th>
                  <th className={headCell}>Open gaps</th>
                  <th className={headCell}>Writes to review</th>
                  <th className={headCell}>Last sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#2d2236]">
                {list?.items.map((item) => (
                  <tr
                    key={item.id}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-[#120e1b] ${selected === item.id ? 'bg-gray-50 dark:bg-[#120e1b]' : ''}`}
                    onClick={() => setSelected(item.id)}
                  >
                    <td className={cell}>
                      <span className="font-medium">
                        {item.display_name ?? item.external_account_id}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {PROVIDERS[item.provider] ?? item.provider}
                      </span>
                    </td>
                    <td className={cell}>{item.shop_name ?? item.shop_id}</td>
                    <td className={cell}>
                      <Badge value={item.status} />
                      {item.last_error && (
                        <span className="block max-w-xs truncate text-xs text-red-600 dark:text-red-400">
                          {item.last_error}
                        </span>
                      )}
                    </td>
                    <td className={cell}>{item.synced_listings}</td>
                    <td className={cell}>{item.open_gaps}</td>
                    <td className={cell}>
                      {item.outbox_attention > 0 ? (
                        <span className="font-semibold text-amber-700 dark:text-amber-300">
                          {item.outbox_attention}
                        </span>
                      ) : (
                        0
                      )}
                    </td>
                    <td className={cell}>{date(item.last_sync_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list && !list.items.length && (
              <p className="p-4 text-sm text-gray-400">No integrations match these filters.</p>
            )}
            {!list && !error && <p className="p-4 text-sm text-gray-400">Loading…</p>}
          </section>
          {list && pages > 1 && (
            <div className="flex items-center justify-between">
              <CustomButton
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </CustomButton>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {page} of {pages}
              </span>
              <CustomButton
                variant="secondary"
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </CustomButton>
            </div>
          )}
          {selected && <Detail id={selected} onClose={() => setSelected(null)} />}
        </>
      )}
      {tab === 'outbox' && (
        <>
          <label className="flex w-fit flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
            <select
              className={selectClass}
              value={outboxStatus}
              onChange={(e) => setOutboxStatus(e.target.value as OutboxStatus)}
            >
              {OUTBOX_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </select>
          </label>
          <section className={`${card} p-4`}>
            {outbox ? (
              <OutboxTable
                rows={outbox}
                storeNames={Object.fromEntries(
                  (list?.items ?? []).map((item) => [
                    item.id,
                    item.display_name ?? item.external_account_id,
                  ])
                )}
              />
            ) : (
              !error && <p className="text-sm text-gray-400">Loading…</p>
            )}
          </section>
        </>
      )}
    </div>
  )
}
